import * as THREE from 'three';
import * as TSL from 'three/tsl';
import { MeshBasicNodeMaterial } from 'three/webgpu';

import { EngineClass } from '../../../systems/ClassRegistry.js';
import { property } from '../../../utils/serialization/decorator.js';
import { ISerializableObjectTag } from '../../../utils/serialization/serializable.js';

import { NodeMaterialAsset } from './NodeMaterialAsset.js';

import type { IDumper, ILoader } from '../../../utils/serialization/factories.js';

/** Vertical object-space gradient between two colors on `MeshBasicNodeMaterial`. */
@EngineClass('ColorGradientNodeMaterialAsset', {
  isNodeMaterialAsset: true,
  nodeMaterialDisplayName: 'Color Gradient',
  nodeMaterialGroup: 'TSL Node FX',
})
export class ColorGradientNodeMaterialAsset extends NodeMaterialAsset(MeshBasicNodeMaterial) {
  readonly [ISerializableObjectTag] = true as const;

  @property({ type: 'color', description: 'Bottom color (Y = -1)' })
    colorBottom = new THREE.Color(0.0, 0.0, 0.0);

  @property({ type: 'color', description: 'Top color (Y = +1)' })
    colorTop = new THREE.Color(1.0, 1.0, 1.0);

  constructor() {
    super();
    this.rebuild();
  }

  override rebuild(): void {
    this.applyCommonMaterialState();
    const t = TSL.positionLocal.y.mul(TSL.float(0.5)).add(TSL.float(0.5));
    const colorA = TSL.color(this.colorBottom);
    const colorB = TSL.color(this.colorTop);
    this.colorNode = TSL.mix(colorA, colorB, t);
  }

  public serialize(dumper: IDumper): void {
    this.serializeAuthoredFields(dumper);
  }

  public static staticDeserialize(data: unknown, loader: ILoader): ColorGradientNodeMaterialAsset {
    const instance = new ColorGradientNodeMaterialAsset();
    ColorGradientNodeMaterialAsset.loadAuthoredFields(instance, loader);
    instance.rebuild();
    return instance;
  }
}
